import { Accordion, Button, Field, RadioGroup } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import { accountAmount, platformCatalog, type PlatformCatalog } from "./modelSupply";
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../lib/api";
import { Icon } from "../components/Icon";
import { useDraft } from "../lib/drafts";
import { connectionHref } from "../connections/navigation";
import { ErrorState } from "../shell/ui";
import type { AgentReadiness } from "./agentReadiness";
import { LocalClientSetup, clientName, useLocalClients } from "./LocalClients";
import { hub, agentHref, hubSettingsHref, instanceStatus, localRuntimeApplied, type AgentInstance, type CreatedAgent } from "./api";
import "./agent-creation.css";

type Stage = "stale" | "failed" | "preparing" | "connecting" | "ready" | "no-model" | "waiting-client" | "owner-stopped";
const STAGE_WORDS: Record<Stage, string> = {
  stale: "连接中断，正在重新确认", failed: "启动未完成", preparing: "正在准备", connecting: "正在连接",
  ready: "已就绪，正在进入", "no-model": "已创建，连接模型后即可开始", "waiting-client": "等待电脑上线",
  "owner-stopped": "停止请求已保存",
};
function creationStage(agent: AgentInstance, stale: boolean, readiness?: AgentReadiness): Stage {
  if (stale) return "stale";
  if (agent.desired_running === false) return "owner-stopped";
  if (agent.runner?.last_error || (localRuntimeApplied(agent) && agent.local_runtime?.state === "error")) return "failed";
  if (agent.location === "local" && agent.client?.online === false) return "waiting-client";
  if (!agent.online) return "preparing";
  if (readiness?.state === "ready") return "ready";
  if (readiness?.state === "no-model") return "no-model";
  return "connecting";
}

export function AgentCreation({ mode, onCreated, onRefresh, verifyAccount, accountId = "local", preparing,
  readiness, preparationStale = false }: {
  mode: "create" | "connect"; onCreated: (receipt: CreatedAgent, continueSetup: boolean) => void;
  onRefresh: () => void; verifyAccount: () => Promise<boolean>; accountId?: string; accountName?: string;
  preparing?: AgentInstance; readiness?: AgentReadiness; preparationStale?: boolean;
}) {
  const directory = useLocalClients();
  const [name, setName, draftError] = useDraft("hub:agent-name:" + accountId, "", "/");
  const [location, setLocation] = useState<"cloud" | "local">(mode === "connect" ? "local" : "cloud");
  const [clientId, setClientId] = useState(() => new URLSearchParams(window.location.hash.split("?")[1]).get("client_id") || "");
  const selectedClient = directory.clients?.find(client => client.id === clientId);
  useEffect(() => {
    if (!clientId && directory.clients?.length === 1) setClientId(directory.clients[0].id);
  }, [clientId, directory.clients]);
  const [assembly, setAssembly] = useState("");
  const [assemblies, setAssemblies] = useState<{ key: string; title: string; description: string }[]>([]);
  const [supply, setSupply] = useState<PlatformCatalog | null>(null);
  const [assembliesStatus, setAssembliesStatus] = useState<"loading" | "ready" | "error">("loading");
  const [assembliesError, setAssembliesError] = useState<unknown>(null);
  const [assembliesAttempt, setAssembliesAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    setAssembliesStatus("loading"); setAssembliesError(null);
    hub.assemblies().then(data => {
      if (!active) return;
      setAssemblies(data.assemblies);
      const catalog = platformCatalog(data.model_supply);
      if (data.model_supply !== undefined && !catalog) throw new ApiError("平台服务未读完整", "请重新读取后再创建。", null, 502);
      setSupply(catalog || null);
      setAssembly(data.assemblies.some(item => item.key === data.default) ? data.default : "");
      if (!data.assemblies.length) throw new ApiError("暂时无法创建", "还没有可选的用途，请稍后重试。", null, 503);
      setAssembliesStatus("ready");
    }).catch(failure => { if (active) { setAssembliesError(failure); setAssembliesStatus("error"); } });
    return () => { active = false; };
  }, [assembliesAttempt]);
  const [submitting, setBusy] = useState(false);
  const [diagnostics, setDiagnostics] = useState<string[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [uncertain, setUncertain] = useState(false);
  const live = useRef(true);
  useEffect(() => { live.current = true; return () => { live.current = false; }; }, []);
  const busy = submitting || Boolean(preparing);
  const stage = preparing ? creationStage(preparing, preparationStale, readiness) : null;
  const cloud = location === "cloud";
  const assemblySelected = assemblies.some(item => item.key === assembly);
  const unavailable = assembliesStatus !== "ready" || !assemblySelected || (!cloud && (!selectedClient || Boolean(directory.error)));
  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || uncertain || unavailable) return;
    setBusy(true); setError(null);
    let sent = false;
    try {
      if (!(await verifyAccount()) || !live.current) return;
      sent = true;
      const receipt = await hub.create(name.trim(), location, assembly, cloud ? undefined : selectedClient!.id);
      if (live.current) setName("");
      onCreated(receipt, live.current);
    } catch (failure) {
      if (live.current) {
        setError(failure);
        if (sent && (!(failure instanceof ApiError) || failure.status >= 500 || failure.status === 0)) setUncertain(true);
      }
      onRefresh();
    } finally { if (live.current) setBusy(false); }
  };
  const noAllowance = supply?.usage.allowance_cny === 0;
  const stageWords = stage === "waiting-client" ? "等待 " + (preparing?.client?.name || selectedClient?.name || "电脑") + " 上线"
    : stage === "owner-stopped" && preparing ? instanceStatus(preparing).label : stage ? STAGE_WORDS[stage] : "";
  return <section className="agent-creation agent-create-page">
    <a className="agent-back" href="/#/agents"><Icon name="back" size={16} />返回</a>
    <header className="agent-create-head"><h1>新建 Agent</h1></header>
    <form className="agent-create-fields" onSubmit={submit} aria-busy={busy || assembliesStatus === "loading"}>
      <Field className="agent-name-field" label="名称" hint="可稍后修改">
        <input aria-label="Agent 名称" placeholder="例如：历史下饭" value={preparing?.name ?? name}
          onChange={event => setName(event.target.value)} disabled={busy || uncertain} />
      </Field>
      {draftError ? <p role="alert">{draftError}</p> : null}
      <fieldset className="agent-capabilities" disabled={busy || uncertain}>
        <legend>用途</legend>
        {assembliesStatus === "loading" ? <p role="status">正在读取可选用途…</p> : assembliesStatus === "error"
          ? <ErrorState error={assembliesError} onRetry={() => setAssembliesAttempt(value => value + 1)} />
          : <RadioGroup label="用途" name="agent-assembly" orientation="horizontal" className="agent-capability-options" value={assembly}
            onValueChange={setAssembly} items={assemblies.map(item => ({ value: item.key, label: item.title, description: item.description }))}
            disabled={busy || uncertain} />}
      </fieldset>
      <fieldset className="agent-location" disabled={busy || uncertain}>
        <legend>运行位置</legend>
        <RadioGroup label="运行位置" name="agent-location" orientation="horizontal" className="agent-run-location-options" value={location} disabled={busy || uncertain}
          onValueChange={value => setLocation(value as "cloud" | "local")} items={[
            { value: "cloud", label: "云端托管" }, { value: "local", label: "自己的电脑" },
          ]} />
        {!cloud ? directory.clients === null && !directory.error ? <p role="status">正在读取我的电脑…</p>
          : directory.clients?.length ? <Field label="电脑">
            <select className="text-input" aria-label="运行客户端" value={clientId} onChange={event => setClientId(event.target.value)} disabled={busy || uncertain}>
              <option value="">选择电脑</option>{directory.clients.map(client => <option key={client.id} value={client.id}>
                {clientName(client, directory.clients || [])}{client.online ? " · 在线" : " · 离线"}
              </option>)}
            </select>
          </Field> : !directory.error ? <LocalClientSetup /> : null : null}
        {!cloud && directory.error ? <div className="settings-actions"><span role="alert">电脑列表暂未更新。</span>
          <Button variant="secondary" onClick={directory.refresh}>重试</Button></div> : null}
        {!cloud && selectedClient && !selectedClient.online && !preparing ? <p className="settings-hint">打开这台电脑上的客户端后自动继续。<a href="/#/clients">重新连接</a></p> : null}
        {!cloud && directory.clients?.length ? <details><summary>连接另一台电脑</summary><LocalClientSetup /></details> : null}
        {!cloud ? <a className="agent-create-clients-link" href="/#/clients">我的电脑</a> : null}
      </fieldset>
      {cloud && supply?.offered.length ? <p className="agent-create-supply">涟漪提供{supply.offered.map(kind => kind.title).join("、")} · 账号可用 {accountAmount(supply.usage.remaining_cny)}</p> : null}
      {noAllowance ? <p className="agent-create-warning" role="status"><Icon name="alert" size={18} />{supply?.free_chat_available
        ? "当前可使用零售价对话模型；收费制作服务需另有可用额度。" : "零售价模型和自带服务不受限制；平台收费服务需有可用额度。"}</p> : null}
      <div className="agent-create-feedback" aria-live="polite">
        {preparing && stage ? <div className="creation-progress" role="status" data-testid="creation-progress" data-stage={stage}>
          {["preparing", "connecting", "ready"].includes(stage) ? <LightMotion state="working" compact /> : null}
          <span>{stageWords}</span>
          {stage === "waiting-client" ? <p>打开客户端后自动继续。</p> : null}
          {stage === "stale" || stage === "failed" ? <Button variant="secondary" onClick={onRefresh}>重新核对</Button> : null}
          {stage === "failed" ? <Accordion value={diagnostics} onValueChange={setDiagnostics}
            items={[{ value: "startup", title: "查看原因", content: <p data-quoted="true">{preparing.local_runtime?.error || preparing.runner?.last_error}</p> }]} /> : null}
          {stage === "no-model" || stage === "connecting" ? <div className="creation-progress-actions">
            {readiness?.state === "no-model" ? <a className="btn btn-accent" href={hubSettingsHref(preparing.id, connectionHref(readiness.namespace, undefined, ""))}>连接对话模型</a> : null}
            <a className="btn" href={agentHref(preparing.id)}>进入工作台</a>
          </div> : null}
        </div> : null}
        {error ? <ErrorState error={error} /> : null}
        {uncertain ? <div className="hub-notice" role="alert"><p>创建结果尚未确认，请先查看 Agent 列表，避免重复创建。</p>
          <a className="btn" href="/#/agents">查看 Agent 列表</a></div> : null}
      </div>
      {!uncertain && !preparing ? <div className="agent-create-submit"><Button type="submit" disabled={busy || unavailable} aria-busy={busy || undefined}>
        {busy ? "正在创建…" : "创建 Agent"}</Button></div> : null}
    </form>
  </section>;
}
